import usePreviousEffect from "@pathwright/ui/src/components/hooks/usePreviousEffect"
import { ConnectStores } from "lib/core/store"
import { DESIGN, LEARN, TEACH } from "path/constants"
import { useEffect, useState } from "react"
import isEqual from "react-fast-compare"
import Step from "./Step"

// TODO: break this out into several container components:
// - <ContentContainer />
// - <AncillaryConainer />
// - <DiscussionContainer />

export const getCompletionResetAfterFailedCompletionAttempt = ({
  completion,
  prevCompletion
}) => {
  let completionResetAfterFailedCompletionAttempt = false
  // handle disabling completion after failed completion attempt
  if (completion && prevCompletion && !isEqual(completion, prevCompletion)) {
    const now = new Date()
    completionResetAfterFailedCompletionAttempt =
      completion.has_failed_passing_attempt &&
      // and completion date has changed?
      !moment(prevCompletion.last_completion_attempt_date || now).isSame(
        completion.last_completion_attempt_date || now
      )
  }

  return completionResetAfterFailedCompletionAttempt
}

// check various progress/completion states to determine if step completion
// is disabled
const getIsCompletionDisabled = ({
  content,
  completion,
  prevCompletion,
  blocksCompletion
}) => {
  let isCompletionDisabled = null

  if (completion) {
    // under no circumstances can a step requiring api completino be completed by user
    if (completion.requires_api_completion) {
      isCompletionDisabled = true
    } else {
      if (content && content.type !== "blocks") {
        // Simply checking content.is_content_completed.
        isCompletionDisabled = !content.is_content_completed
      }

      // handle disabling completion after failed completion attempt
      if (
        getCompletionResetAfterFailedCompletionAttempt({
          completion,
          prevCompletion
        })
      ) {
        isCompletionDisabled = true
      }

      // handle disabling/enabling completion after blocks completion progress has changed
      if (blocksCompletion) {
        // This is a bit hacky but whatever... basically if the block content
        // has trackable progress (progress !== null), then disable/enable the button
        // based on the completion progress.
        isCompletionDisabled =
          blocksCompletion.progress !== null && blocksCompletion.progress < 1
      }
    }
  }

  return isCompletionDisabled
}

const StepContainer = ({
  step,
  content,
  isActive,
  isAdjacentIndex,
  onContentSaved,
  ...rest
}) => {
  const [completionToken, setCompletionToken] = useState(null)
  // only default completion to disabled if step has content,
  // or put differently, allow for steps that lack content to be completed
  // (since we don't currently require steps to have content,
  // it's necessary to allow completing a step if it does not have content)
  const [isCompletionDisabled, setIsCompletionDisabled] = useState(
    !!step.content_id
  )
  const handleIsCompletionDisabled = (completionData) => {
    const nextIsCompletionDisabled = getIsCompletionDisabled(completionData)
    if (nextIsCompletionDisabled !== null) {
      setIsCompletionDisabled(nextIsCompletionDisabled)
    }
  }

  const handleLegacyContentCompletion = (isCompletionDisabled) => {
    // implicitly disabling completion if not explicitly supplied
    if (typeof isCompletionDisabled !== "boolean") {
      isCompletionDisabled = getIsCompletionDisabled({
        content,
        completion: step.completion
      })
    }

    setIsCompletionDisabled(isCompletionDisabled)
  }

  // hack for enabling typeform to reload. Otherwise typeform is blank if it had previously been loaded
  useEffect(() => {
    if (window.typeformEmbedIsloaded) {
      window.typeformEmbedIsloaded = false
      const typeformScript = document.getElementById("typef_orm")
      if (typeformScript) {
        return typeformScript.remove()
      }
    }
  }, [])

  useEffect(() => {
    // handle enabling/disabling completion after step content changes
    handleIsCompletionDisabled({
      content,
      completion: step.completion
    })
  }, [content])

  usePreviousEffect(
    ([prevCompletion]) => {
      // handle enabling/disabling completion after step completion changes
      handleIsCompletionDisabled({
        completion: step.completion,
        prevCompletion
      })
    },
    [step.completion]
  )

  const handleBlocksCompletion = (blocksCompletion) => {
    // handle enabling/disabling completion after blocks completion changes
    handleIsCompletionDisabled({
      content,
      completion: step.completion,
      blocksCompletion
    })
    setCompletionToken(blocksCompletion.completionToken)
  }

  const handleContentSaved = ({ content }) => {
    onContentSaved({ content })
  }

  return (
    <Step
      key={step.id}
      {...rest}
      onContentSaved={handleContentSaved}
      step={step}
      content={content}
      isActive={isActive}
      isVisible
      isAdjacentIndex={isAdjacentIndex}
      handleBlocksCompletion={handleBlocksCompletion}
      handleLegacyContentCompletion={handleLegacyContentCompletion}
      isCompletionDisabled={isCompletionDisabled}
      completionToken={completionToken}
    />
  )
}

StepContainer.displayName = "StepContainer"

const getStudentBools = function ({
  step,
  group,
  completion,
  isActive,
  canRegisterForOffering,
  nonPassiveStepStarts,
  canParticipate
}) {
  let areNotesVisible,
    isCompletionVisible,
    isContentVisible,
    isDiscussionQuestionsVisible,
    isDiscussionPromptsVisible
  const { is_locked, permissions } = step
  const { can_complete, can_discuss, can_note, can_view_content } = permissions

  const isPermissionLocked = !can_view_content
  const isStartLocked = !!nonPassiveStepStarts
  const isPasswordLocked = is_locked
  const canPostDiscussionQuestion = false
  const canPostQuestion = canParticipate

  // we have to do a calculated check here because of admin permissions
  const isLocked = isPasswordLocked || isPermissionLocked || isStartLocked

  // Visibility checks
  if (!isLocked) {
    isContentVisible = can_view_content
    isDiscussionQuestionsVisible = isActive && can_discuss
    isDiscussionPromptsVisible = isActive && can_discuss
    isCompletionVisible = isActive && can_complete
    areNotesVisible = isActive && can_note
  } else {
    // Hide all the things
    isContentVisible =
      isCompletionVisible =
      isDiscussionQuestionsVisible =
      isDiscussionPromptsVisible =
        false
  }

  const isRegisterLocked =
    !canRegisterForOffering && !isContentVisible && !can_view_content

  return {
    isPermissionLocked,
    isStartLocked,
    isLocked,
    isPasswordLocked,
    isContentVisible,
    isCompletionVisible,
    isDiscussionQuestionsVisible,
    isDiscussionPromptsVisible,
    areNotesVisible,
    canPostDiscussionQuestion,
    canPostQuestion,
    isRegisterLocked
  }
}

const getAdminBools = function ({
  step,
  isActive,
  mode,
  contentSelection,
  selection,
  editing_selection
}) {
  const isEditingMode = mode === DESIGN
  const isEditingStep =
    isActive &&
    (selection != null ? selection.id : undefined) ===
      (step != null ? step.id : undefined) &&
    editing_selection
  const isEditingContent = isActive && contentSelection
  const canPostDiscussionQuestion =
    mode > LEARN && (step != null ? step.permissions.can_discuss : undefined)
  const canPostQuestion = true

  let adminBools = {
    isEditingMode,
    isEditingStep,
    isEditingContent,
    canPostDiscussionQuestion,
    canPostQuestion
  }

  if (isEditingMode || mode === TEACH) {
    // Turn off locking/hiding for most things when editing
    adminBools = _.assign({}, adminBools, {
      isPermissionLocked: false,
      isStartLocked: false,
      isPasswordLocked: false,
      isContentVisible: true,
      isCompletionVisible: false,
      isDiscussionQuestionsVisible: !!step && step.permissions.can_discuss, // if discussion is disabled in resource or offering, then no
      isDiscussionPromptsVisible: !!step && step.permissions.can_discuss, // if discussion is disabled in resource or offering, then no
      isLocked: false,
      areNotesVisible: true,
      isStartLocked: false
    })
  }

  return adminBools
}

const mapStateToProps = function (state, ownProps) {
  let completion, content, content_error, downloads, notes
  const { selection, editing_selection } = state.pathAdminStore.state
  const { step: selectedStep, group, active_step_index } = state.stepStore.state
  const { loadStep } = state.stepStore.action
  const { loadPathItem } = state.pathStore.action
  const { mode } = state.pathStore.state
  const { resource, offering } = state.resourceStore.state
  let { step } = ownProps

  // Save blocks content after the first block is added
  const { selectBlocksContent } = state.contentAdminStore.action
  const onContentSaved = ({ content }) => {
    // This is "new" content (ie, the Path Item didn't know about it before)...
    // if the incoming "content" (from Firebase) exists w/ blocks and
    // the step doesn't have content.id or the content type isn't "blocks"
    const isNewContent =
      content &&
      content.blocks.length > 0 &&
      (!step.content || !step.content.id || step.content.type !== "blocks")

    if (isNewContent) {
      selectBlocksContent()
    }

    // Don't do anything if content isn't new (Blocks saves its own content)
  }

  const canRegisterForOffering =
    state.resourceStore.request.canRegisterForOffering()
  let isActive =
    ownProps.step.sync_source_id ===
    (selectedStep != null ? selectedStep.sync_source_id : undefined)
  const offeringPermissions = state.resourceStore.request.permissions()
  const canFacilitate = __guard__(offeringPermissions, (x) => x.can_facilitate)
  const canParticipate = __guard__(
    offeringPermissions,
    (x) => x.can_participate
  )

  let nonPassiveStepStarts = null
  if (step.starts) {
    nonPassiveStepStarts =
      !step.passive_starts && moment(step.starts).isAfter() ? step.starts : null
  } else if (!nonPassiveStepStarts && group.starts) {
    nonPassiveStepStarts =
      !group.passive_starts && moment(group.starts).isAfter()
        ? group.starts
        : null
  } else if (!nonPassiveStepStarts && offering && offering.access_starts) {
    nonPassiveStepStarts = moment(offering.access_starts).isAfter()
      ? offering.access_starts
      : null
  }

  if (isActive) {
    ;({ step, downloads, notes, content, content_error, completion } =
      state.stepStore.state)

    if (step == null) {
      // unmounting, reset to load state
      isActive = false
      ;({ step } = ownProps)
    }
  }

  const reloadStep = () => loadStep()

  // Calculated state booleans
  const defaults = {
    isEditingMode: false,
    isEditingStep: false,
    isEditingContent: false
  }

  let bools = _.assign(
    {},
    defaults,
    getStudentBools({
      step,
      completion,
      isActive,
      downloads,
      canRegisterForOffering,
      nonPassiveStepStarts,
      canParticipate
    })
  )

  if (canFacilitate) {
    bools = _.assign(
      {},
      bools,
      getAdminBools({
        step,
        completion,
        isActive,
        mode,
        contentSelection: state.contentAdminStore.state.selection,
        selection,
        editing_selection
      })
    )
  }

  if (mode !== LEARN) {
    bools.isCompletionVisible = false
  }

  if (mode === DESIGN) {
    bools.isDiscussionQuestionsVisible = false
  }

  // detect whether or not to show license attribution
  bools.isLicensed = !!resource.licensed_from_resource

  return _.assign(
    {},
    ownProps,
    {
      step,
      mode,
      notes,
      content,
      downloads,
      completion,
      content_error,
      reloadStep,
      onContentSaved,
      isActive,
      nonPassiveStepStarts
    },
    bools
  )
}

const stepWatchKeys = [
  "step",
  "notes",
  "content",
  "completion",
  "content_error",
  "active_step_index",
  "downloads"
]

const stores = [
  { store: "step", watch: stepWatchKeys },
  { store: "path", watch: ["mode"] },
  { store: "pathAdmin", watch: ["editing_selection"] },
  { store: "contentAdmin", watch: ["selection"] },
  { store: "resource", watch: false }
]

export default ConnectStores(StepContainer, stores, {}, mapStateToProps)

function __guard__(value, transform) {
  return typeof value !== "undefined" && value !== null
    ? transform(value)
    : undefined
}
